import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Modal, Spin, Table, Tag, Upload, message } from 'antd';
import useEmployeeNoticesT, { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import {
  downloadEquipmentTemplate,
  previewEquipmentImport,
} from '@/services/equipmentRequests.service';

const MODE_KEYS = { multi: 'groupModeMulti', single: 'groupModeSingle', yesNo: 'groupModeYesNo' };

/**
 * "Uvezi iz Excela" on the equipment settings: download the sheet (the
 * current list already in it), pick the filled-in file, see every row with
 * its problems, then "Primeni" puts the groups and items in the form —
 * replacing the list there; "Sačuvaj" on the settings writes them. Nothing
 * is saved here.
 *
 * @param {Array<{key:string, name:string}>} props.services - The form's services, saved or not.
 * @param {(plan: {groups:Array, items:Array}) => void} props.onApply
 */
export default function EquipmentImportModal({ companyId, open, onClose, services, onApply }) {
  const { tn } = useEmployeeNoticesT();
  const { i18n } = useTranslation();
  const [plan, setPlan] = useState(null);
  const ti = (key, opts) => tn(`equipmentImport.${key}`, opts);

  const template = useMutation({
    mutationFn: () => downloadEquipmentTemplate(companyId, i18n.language),
    onError: (error) => message.error(errorText(error, ti('failed'))),
  });

  const preview = useMutation({
    mutationFn: (file) => previewEquipmentImport(companyId, file, services),
    onSuccess: setPlan,
    onError: (error) => {
      message.error(errorText(error, ti('failed')));
      setPlan(null);
    },
  });

  const columns = [
    { title: ti('colRow'), dataIndex: 'row', width: 64 },
    { title: ti('colGroup'), dataIndex: 'group', render: (v) => v || '—' },
    { title: ti('colItem'), dataIndex: 'item', render: (v) => v || '—' },
    {
      title: ti('colMode'),
      dataIndex: 'mode',
      width: 120,
      render: (v) => (MODE_KEYS[v] ? tn(MODE_KEYS[v]) : '—'),
    },
    {
      title: ti('colDetail'),
      dataIndex: 'detail',
      width: 80,
      render: (v) => (v ? tn(v === 'yes' ? 'answerYes' : 'answerNo') : ''),
    },
    { title: ti('colService'), dataIndex: 'service', width: 120, render: (v) => v || '—' },
    {
      title: ti('colStatus'),
      dataIndex: 'errors',
      width: 220,
      render: (errors) =>
        errors?.length ? (
          <div className="flex flex-wrap gap-1">
            {errors.map((code) => (
              <Tag key={code} color="red" className="!mr-0 !whitespace-normal">
                {ti(`errors.${code}`)}
              </Tag>
            ))}
          </div>
        ) : (
          <Tag color="green">{ti('rowOk')}</Tag>
        ),
    },
  ];

  const groupsCount = plan?.groups?.length || 0;
  const itemsCount = plan?.items?.length || 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterClose={() => setPlan(null)}
      width={plan ? 980 : 560}
      title={ti('title')}
      okText={ti('apply')}
      cancelText={tn('cancel')}
      okButtonProps={{
        disabled: !plan || plan.errorCount > 0 || !groupsCount,
        className: '!rounded-[50px]',
      }}
      cancelButtonProps={{ className: '!rounded-[50px]' }}
      onOk={() => {
        onApply({ groups: plan.groups, items: plan.items });
        onClose();
      }}
    >
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[12.5px] text-zinc-600">{ti('templateHint')}</span>
          <Button
            size="small"
            className="!rounded-[50px]"
            icon={<DownloadOutlined />}
            loading={template.isPending}
            onClick={() => template.mutate()}
          >
            {ti('downloadTemplate')}
          </Button>
        </div>

        <Upload.Dragger
          accept=".xlsx"
          maxCount={1}
          showUploadList={false}
          beforeUpload={(file) => {
            preview.mutate(file);
            return false;
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{ti('pickFile')}</p>
        </Upload.Dragger>

        {preview.isPending && (
          <div className="flex justify-center py-4">
            <Spin />
          </div>
        )}

        {plan && (
          <>
            {plan.errorCount > 0 ? (
              <Alert type="error" showIcon message={ti('hasErrors', { count: plan.errorCount })} />
            ) : (
              <Alert
                type="info"
                showIcon
                message={ti('readyToApply', { groups: groupsCount, items: itemsCount })}
              />
            )}
            <Table
              size="small"
              rowKey="row"
              columns={columns}
              dataSource={plan.rows}
              pagination={plan.rows.length > 50 ? { pageSize: 50, showSizeChanger: false } : false}
              scroll={{ y: 420 }}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
